// Team admin — Phase 2.3 of the data spine (docs/data-spine-plan.md).
//
// Ken-facing editor for the people/team model that no upstream data source
// knows: roles, teams (pods), and — the point of this whole surface — MINI-PODS.
// Backed by server/people.js:
//   GET  /api/people                          → { people: [...] }
//   POST /api/people/seed                     → { ok, summary }        (admin/ops)
//   POST /api/people/:accountId               → person field updates   (admin/ops)
//   POST /api/people/:accountId/membership    → set/close a membership (admin/ops)
//
// Reads are open to any authed user (requireDashAccess); writes are gated to
// admin/ops (requireWrite → 403). Viewers see the table read-only.

import { useState, useEffect, useCallback } from 'react'
import { PULSE_COLORS } from './pulseConfig.js'

const C = PULSE_COLORS

const ROLE_OPTIONS = ['dev', 'qa', 'manager', 'other']
// Framework a membership runs under: 'jira' = mini-pod teams tracked in Jira,
// 'ai-first' = gogo-crew teams tracked in the bug-queue. See people.js.
const FRAMEWORK_OPTIONS = ['jira', 'ai-first']
// Distinct pods the app knows; unioned at render with whatever teams the data
// already carries, so an existing team value never disappears from its select.
const BASE_TEAMS = ['buyer', 'seller', 'foundations', 'data', 'data-engineering', 'other']

// Editable slice of a row, used both for the dirty check and for diffing on save.
function editableOf(row) {
    return {
        display_name: row.display_name ?? '',
        email: row.email ?? '',
        gitlab_username: row.gitlab_username ?? '',
        role: row.role ?? 'dev',
        active: !!row.active,
        _team: row._team ?? '',
        _miniPod: row._miniPod ?? '',
        _framework: row._framework ?? 'jira',
    }
}

// Flatten a server person into an editable row (current membership → _team/_miniPod).
function toRow(p) {
    const current = Array.isArray(p.memberships) ? p.memberships[0] : null
    return {
        ...p,
        _team: current?.team ?? '',
        _miniPod: current?.mini_pod ?? '',
        _framework: current?.framework ?? 'jira',
        _extraTeams: Array.isArray(p.memberships) ? p.memberships.length - 1 : 0,
    }
}

const norm = (v) => (v === null || v === undefined ? '' : String(v))

// Stable identity for a proposal / pin selection: unit + framework.
const proposalKey = (p) => `${p.unit_type}|${p.unit_key}|${p.framework}`

// Compact metric formatting for the proposal + pins tables.
const fmtMetric = (metric, value) => {
    if (value == null || !Number.isFinite(Number(value))) return '—'
    const n = Number(value)
    if (metric === 'escape_rate') return `${(n * 100).toFixed(1)}%`
    if (metric.startsWith('lead_time')) return `${n.toFixed(1)}h`
    return n.toFixed(2)
}
const fmtDate = (v) => {
    if (!v) return '—'
    const d = new Date(v)
    return Number.isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toISOString().slice(0, 10)
}
// Order metrics predictably in the proposal columns.
const METRIC_ORDER = ['throughput_per_week', 'lead_time_p50_hours', 'lead_time_p85_hours', 'escape_rate']
const METRIC_LABEL = {
    throughput_per_week: 'Thru/wk',
    lead_time_p50_hours: 'Lead p50',
    lead_time_p85_hours: 'Lead p85',
    escape_rate: 'Escape',
}

export default function TeamAdmin({ authUser }) {
    const canEdit = ['admin', 'ops'].includes(authUser?.role)

    const [rows, setRows] = useState([])
    const [baseline, setBaseline] = useState({}) // accountId → JSON string of editableOf() at last sync
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [notConfigured, setNotConfigured] = useState(false)
    const [readOnly, setReadOnly] = useState(!canEdit) // flips true on a 403 too
    const [savingId, setSavingId] = useState(null)
    const [rowMsg, setRowMsg] = useState({}) // accountId → { type: 'ok'|'err', text }
    const [seedBusy, setSeedBusy] = useState(false)
    const [seedResult, setSeedResult] = useState(null)

    const applyPeople = useCallback((people) => {
        const nextRows = people.map(toRow)
        const nextBaseline = {}
        for (const r of nextRows) nextBaseline[r.account_id] = JSON.stringify(editableOf(r))
        setRows(nextRows)
        setBaseline(nextBaseline)
    }, [])

    const fetchPeople = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await fetch('/api/people', { credentials: 'include' })
            if (res.status === 503) {
                setNotConfigured(true)
                setRows([])
                return
            }
            if (!res.ok) throw new Error(`Failed to load people (${res.status})`)
            const data = await res.json()
            const people = Array.isArray(data) ? data : data.people || []
            setNotConfigured(false)
            applyPeople(people)
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }, [applyPeople])

    useEffect(() => {
        fetchPeople()
    }, [fetchPeople])

    // ── Row editing ──────────────────────────────────────────────────────────
    const setField = (id, field, value) => {
        setRows((prev) => prev.map((r) => (r.account_id === id ? { ...r, [field]: value } : r)))
    }

    const isDirty = (row) => baseline[row.account_id] !== JSON.stringify(editableOf(row))

    // Union of BASE_TEAMS with any team values already present in the data.
    const teamOptions = (() => {
        const set = new Set(BASE_TEAMS)
        for (const r of rows) if (r._team) set.add(r._team)
        return [...set]
    })()

    const saveRow = async (id) => {
        const row = rows.find((r) => r.account_id === id)
        if (!row) return
        const base = JSON.parse(baseline[id])

        // Person-field diff.
        const personBody = {}
        for (const field of ['display_name', 'email', 'gitlab_username', 'role', 'active']) {
            const cur = editableOf(row)[field]
            if (cur !== base[field]) personBody[field] = cur
        }

        // Membership diff (against baseline = server truth). A mini_pod OR
        // framework change on the same team is a close-and-replace on the server.
        const oldTeam = norm(base._team)
        const newTeam = norm(row._team).trim()
        const oldMini = norm(base._miniPod)
        const newMini = norm(row._miniPod).trim()
        const oldFw = norm(base._framework) || 'jira'
        const newFw = norm(row._framework) || 'jira'
        const membershipOps = []
        if (newTeam !== oldTeam) {
            if (oldTeam) membershipOps.push({ team: oldTeam, end: true })
            if (newTeam) membershipOps.push({ team: newTeam, mini_pod: newMini || null, framework: newFw })
        } else if (newTeam && (newMini !== oldMini || newFw !== oldFw)) {
            membershipOps.push({ team: newTeam, mini_pod: newMini || null, framework: newFw })
        }

        if (Object.keys(personBody).length === 0 && membershipOps.length === 0) return

        setSavingId(id)
        setRowMsg((m) => ({ ...m, [id]: null }))

        const post = async (url, body) => {
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body),
            })
            if (res.status === 403) {
                setReadOnly(true)
                throw new Error('editing requires admin/ops role')
            }
            if (!res.ok) {
                let msg = `save failed (${res.status})`
                try {
                    const j = await res.json()
                    if (j.error) msg = j.error
                } catch {
                    /* keep default */
                }
                throw new Error(msg)
            }
            return res.json()
        }

        try {
            if (Object.keys(personBody).length > 0) {
                await post(`/api/people/${encodeURIComponent(id)}`, personBody)
            }
            for (const op of membershipOps) {
                await post(`/api/people/${encodeURIComponent(id)}/membership`, op)
            }
            // Success: commit this row's editable slice as the new baseline. Other
            // rows' in-progress edits are left untouched (no global refetch).
            setBaseline((b) => ({ ...b, [id]: JSON.stringify(editableOf(row)) }))
            setRowMsg((m) => ({ ...m, [id]: { type: 'ok', text: 'Saved' } }))
        } catch (err) {
            setRowMsg((m) => ({ ...m, [id]: { type: 'err', text: err.message } }))
            // Resync from the server so the row reflects truth after a failure.
            fetchPeople()
        } finally {
            setSavingId(null)
        }
    }

    const runSeed = async () => {
        setSeedBusy(true)
        setSeedResult(null)
        setError(null)
        try {
            const res = await fetch('/api/people/seed', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            })
            if (res.status === 403) {
                setReadOnly(true)
                throw new Error('seeding requires admin/ops role')
            }
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || `seed failed (${res.status})`)
            setSeedResult(data.summary || data)
            await fetchPeople()
        } catch (err) {
            setError(err.message)
        } finally {
            setSeedBusy(false)
        }
    }

    // ── Styles ─────────────────────────────────────────────────────────────────
    const cardStyle = {
        background: C.cardBg,
        borderRadius: '12px',
        padding: '20px',
        border: `1px solid ${C.border}`,
    }
    const th = {
        textAlign: 'left',
        padding: '8px 10px',
        color: C.textMuted,
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        borderBottom: `1px solid ${C.border}`,
        whiteSpace: 'nowrap',
    }
    const td = { padding: '6px 10px', borderBottom: `1px solid ${C.border}`, verticalAlign: 'middle' }
    const inputStyle = {
        background: C.bgInner,
        border: `1px solid ${C.border}`,
        borderRadius: '6px',
        color: C.text,
        padding: '5px 8px',
        fontSize: '12px',
        width: '100%',
        boxSizing: 'border-box',
    }
    const disabledInput = { ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }

    // ── Render ───────────────────────────────────────────────────────────────
    const header = (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '16px',
                marginBottom: '16px',
                flexWrap: 'wrap',
            }}
        >
            <div>
                <h3 style={{ color: C.text, fontWeight: 600, fontSize: '18px', margin: 0 }}>👥 Team &amp; Mini-Pods</h3>
                <p style={{ color: C.textMuted, fontSize: '13px', margin: '6px 0 0', maxWidth: '640px' }}>
                    The people model behind the metrics — roles, teams, and mini-pods. Mini-pod is the one thing no data
                    source tracks; set it here. Edits save per row.
                </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                    onClick={fetchPeople}
                    disabled={loading}
                    style={{
                        background: 'transparent',
                        border: `1px solid ${C.border}`,
                        borderRadius: '8px',
                        color: C.textMuted,
                        padding: '8px 14px',
                        fontSize: '13px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                    }}
                >
                    ↻ Refresh
                </button>
                {!readOnly && (
                    <button
                        onClick={runSeed}
                        disabled={seedBusy}
                        style={{
                            background: seedBusy ? C.border : C.primary,
                            border: 'none',
                            borderRadius: '8px',
                            color: '#fff',
                            padding: '8px 14px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: seedBusy ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {seedBusy ? '⏳ Seeding…' : '🌱 Seed from Jira data'}
                    </button>
                )}
            </div>
        </div>
    )

    const readOnlyNote = readOnly && (
        <div
            style={{
                background: `${C.warning}18`,
                border: `1px solid ${C.warning}55`,
                borderRadius: '8px',
                color: C.warning,
                padding: '8px 12px',
                fontSize: '12px',
                marginBottom: '14px',
            }}
        >
            Editing requires an admin/ops role — this view is read-only for you.
        </div>
    )

    const seedNote = seedResult && (
        <div
            style={{
                background: `${C.success}14`,
                border: `1px solid ${C.success}55`,
                borderRadius: '8px',
                color: C.text,
                padding: '10px 12px',
                fontSize: '12px',
                marginBottom: '14px',
            }}
        >
            <strong style={{ color: C.success }}>Seed complete.</strong>{' '}
            {Object.entries(seedResult)
                .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
                .join(' · ')}
        </div>
    )

    let body
    if (notConfigured) {
        body = (
            <div style={{ color: C.textMuted, fontSize: '13px', padding: '30px 0', textAlign: 'center' }}>
                The people store isn&apos;t available — no database is configured for this environment.
            </div>
        )
    } else if (loading && rows.length === 0) {
        body = (
            <div style={{ color: C.textMuted, fontSize: '13px', padding: '30px 0', textAlign: 'center' }}>
                Loading people…
            </div>
        )
    } else if (rows.length === 0) {
        body = (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: '15px', color: C.text, marginBottom: '6px' }}>No people yet</div>
                <div style={{ fontSize: '13px', color: C.textMuted, marginBottom: '18px' }}>
                    {readOnly
                        ? 'Ask an admin/ops user to seed the people model from Jira data.'
                        : 'Seed the people model from your existing Jira / GitLab data to get started.'}
                </div>
                {!readOnly && (
                    <button
                        onClick={runSeed}
                        disabled={seedBusy}
                        style={{
                            background: seedBusy ? C.border : C.primary,
                            border: 'none',
                            borderRadius: '8px',
                            color: '#fff',
                            padding: '10px 18px',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: seedBusy ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {seedBusy ? '⏳ Seeding…' : '🌱 Seed from Jira data'}
                    </button>
                )}
            </div>
        )
    } else {
        body = (
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                    <thead>
                        <tr>
                            <th style={th}>Name</th>
                            <th style={th}>Email</th>
                            <th style={th}>Role</th>
                            <th style={th}>Team</th>
                            <th style={th}>Mini-pod</th>
                            <th style={th}>Framework</th>
                            <th style={th}>GitLab</th>
                            <th style={th}>Source</th>
                            <th style={{ ...th, textAlign: 'center' }}>Active</th>
                            <th style={{ ...th, textAlign: 'right' }}>Save</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const id = row.account_id
                            const busy = savingId === id
                            const dirty = isDirty(row)
                            const msg = rowMsg[id]
                            const dis = readOnly || busy
                            return (
                                <tr key={id} style={{ opacity: busy ? 0.55 : 1 }}>
                                    <td style={{ ...td, minWidth: '150px' }}>
                                        <input
                                            style={dis ? disabledInput : inputStyle}
                                            value={row.display_name ?? ''}
                                            disabled={dis}
                                            onChange={(e) => setField(id, 'display_name', e.target.value)}
                                        />
                                    </td>
                                    <td style={{ ...td, minWidth: '180px' }}>
                                        <input
                                            style={dis ? disabledInput : inputStyle}
                                            value={row.email ?? ''}
                                            disabled={dis}
                                            onChange={(e) => setField(id, 'email', e.target.value)}
                                        />
                                    </td>
                                    <td style={{ ...td, minWidth: '90px' }}>
                                        <select
                                            style={dis ? disabledInput : inputStyle}
                                            value={row.role ?? 'dev'}
                                            disabled={dis}
                                            onChange={(e) => setField(id, 'role', e.target.value)}
                                        >
                                            {ROLE_OPTIONS.map((r) => (
                                                <option key={r} value={r}>
                                                    {r}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td style={{ ...td, minWidth: '130px' }}>
                                        <select
                                            style={dis ? disabledInput : inputStyle}
                                            value={row._team ?? ''}
                                            disabled={dis}
                                            onChange={(e) => setField(id, '_team', e.target.value)}
                                        >
                                            <option value="">— none —</option>
                                            {teamOptions.map((t) => (
                                                <option key={t} value={t}>
                                                    {t}
                                                </option>
                                            ))}
                                        </select>
                                        {row._extraTeams > 0 && (
                                            <div style={{ fontSize: '10px', color: C.textSubtle, marginTop: '2px' }}>
                                                +{row._extraTeams} more
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ ...td, minWidth: '130px' }}>
                                        <input
                                            style={dis ? disabledInput : inputStyle}
                                            placeholder="mini-pod"
                                            value={row._miniPod ?? ''}
                                            disabled={dis || !row._team}
                                            onChange={(e) => setField(id, '_miniPod', e.target.value)}
                                        />
                                    </td>
                                    <td style={{ ...td, minWidth: '110px' }}>
                                        <select
                                            style={dis || !row._team ? disabledInput : inputStyle}
                                            value={row._framework ?? 'jira'}
                                            disabled={dis || !row._team}
                                            onChange={(e) => setField(id, '_framework', e.target.value)}
                                        >
                                            {FRAMEWORK_OPTIONS.map((f) => (
                                                <option key={f} value={f}>
                                                    {f}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td style={{ ...td, minWidth: '130px' }}>
                                        <input
                                            style={dis ? disabledInput : inputStyle}
                                            value={row.gitlab_username ?? ''}
                                            disabled={dis}
                                            onChange={(e) => setField(id, 'gitlab_username', e.target.value)}
                                        />
                                    </td>
                                    <td style={{ ...td, color: C.textSubtle, fontSize: '11px', whiteSpace: 'nowrap' }}>
                                        {row.source || '—'}
                                    </td>
                                    <td style={{ ...td, textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={!!row.active}
                                            disabled={dis}
                                            onChange={(e) => setField(id, 'active', e.target.checked)}
                                        />
                                    </td>
                                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <button
                                            onClick={() => saveRow(id)}
                                            disabled={dis || !dirty}
                                            style={{
                                                background: !dis && dirty ? C.primary : 'transparent',
                                                border: `1px solid ${!dis && dirty ? C.primary : C.border}`,
                                                borderRadius: '6px',
                                                color: !dis && dirty ? '#fff' : C.textSubtle,
                                                padding: '5px 12px',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                cursor: !dis && dirty ? 'pointer' : 'not-allowed',
                                            }}
                                        >
                                            {busy ? '…' : 'Save'}
                                        </button>
                                        {msg && (
                                            <div
                                                style={{
                                                    fontSize: '10px',
                                                    marginTop: '3px',
                                                    color: msg.type === 'ok' ? C.success : C.danger,
                                                }}
                                            >
                                                {msg.text}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        )
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={cardStyle}>
                {header}
                {readOnlyNote}
                {seedNote}
                {error && (
                    <div
                        style={{
                            background: `${C.danger}14`,
                            border: `1px solid ${C.danger}55`,
                            borderRadius: '8px',
                            color: C.danger,
                            padding: '8px 12px',
                            fontSize: '12px',
                            marginBottom: '14px',
                        }}
                    >
                        {error}
                    </div>
                )}
                {body}
            </div>
            <BaselinesPanel canEdit={canEdit} cardStyle={cardStyle} th={th} td={td} />
        </div>
    )
}

// ── Baselines panel ──────────────────────────────────────────────────────────
// Compute → review → pin. The compute endpoint PROPOSES trailing-window numbers;
// a human commits them via POST /api/baselines/pin (baselines stay pinned values,
// per docs/framework-comparison-plan.md Phase A). Existing pins are shown so a
// re-pin reads as a visible overwrite.
function BaselinesPanel({ canEdit, cardStyle, th, td }) {
    const [weeks] = useState(12)
    const [computing, setComputing] = useState(false)
    const [computeErr, setComputeErr] = useState(null)
    const [notConfigured, setNotConfigured] = useState(false)
    const [proposal, setProposal] = useState(null) // { weeks, window_start, window_end, proposals: [...] }
    const [selected, setSelected] = useState({}) // proposalKey → bool
    const [pinning, setPinning] = useState(false)
    const [rowMsg, setRowMsg] = useState({}) // proposalKey → { type, text }
    const [readOnly, setReadOnly] = useState(!canEdit) // flips true on a 403 too

    const [pins, setPins] = useState([])
    const [pinsLoading, setPinsLoading] = useState(true)

    const fetchPins = useCallback(async () => {
        setPinsLoading(true)
        try {
            const res = await fetch('/api/baselines', { credentials: 'include' })
            if (res.status === 503) {
                setNotConfigured(true)
                setPins([])
                return
            }
            if (!res.ok) throw new Error(`Failed to load baselines (${res.status})`)
            const data = await res.json()
            setNotConfigured(false)
            setPins(Array.isArray(data) ? data : data.baselines || [])
        } catch {
            // Non-fatal: the pins table just stays empty.
            setPins([])
        } finally {
            setPinsLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchPins()
    }, [fetchPins])

    const runCompute = async () => {
        setComputing(true)
        setComputeErr(null)
        setRowMsg({})
        try {
            const res = await fetch(`/api/baselines/compute?weeks=${weeks}`, { credentials: 'include' })
            if (res.status === 503) {
                setNotConfigured(true)
                setProposal(null)
                return
            }
            if (!res.ok) throw new Error(`compute failed (${res.status})`)
            const data = await res.json()
            setNotConfigured(false)
            setProposal(data)
            // Default every proposed unit to checked.
            const sel = {}
            for (const p of data.proposals || []) sel[proposalKey(p)] = true
            setSelected(sel)
        } catch (err) {
            setComputeErr(err.message)
        } finally {
            setComputing(false)
        }
    }

    const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }))

    const pinSelected = async () => {
        if (!proposal) return
        const targets = (proposal.proposals || []).filter((p) => selected[proposalKey(p)])
        if (targets.length === 0) return
        setPinning(true)
        for (const p of targets) {
            const key = proposalKey(p)
            setRowMsg((m) => ({ ...m, [key]: { type: 'pending', text: 'pinning…' } }))
            try {
                const res = await fetch('/api/baselines/pin', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        unit_type: p.unit_type,
                        unit_key: p.unit_key,
                        framework: p.framework,
                        window_start: proposal.window_start,
                        window_end: proposal.window_end,
                        metrics: p.metrics.map((m) => ({ metric: m.metric, value: m.value, sample_n: m.sample_n })),
                    }),
                })
                if (res.status === 403) {
                    setReadOnly(true)
                    throw new Error('pinning requires admin/ops role')
                }
                if (!res.ok) {
                    let msg = `pin failed (${res.status})`
                    try {
                        const j = await res.json()
                        if (j.error) msg = j.error
                    } catch {
                        /* keep default */
                    }
                    throw new Error(msg)
                }
                const j = await res.json()
                setRowMsg((m) => ({ ...m, [key]: { type: 'ok', text: `pinned ${j.pinned ?? p.metrics.length}` } }))
            } catch (err) {
                setRowMsg((m) => ({ ...m, [key]: { type: 'err', text: err.message } }))
            }
        }
        setPinning(false)
        await fetchPins()
    }

    const selectedCount = proposal ? (proposal.proposals || []).filter((p) => selected[proposalKey(p)]).length : 0

    const btn = (bg, extra = {}) => ({
        background: bg,
        border: 'none',
        borderRadius: '8px',
        color: '#fff',
        padding: '8px 14px',
        fontSize: '13px',
        fontWeight: 600,
        ...extra,
    })

    const panelHeader = (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '16px',
                marginBottom: '16px',
                flexWrap: 'wrap',
            }}
        >
            <div>
                <h3 style={{ color: C.text, fontWeight: 600, fontSize: '18px', margin: 0 }}>📌 Baselines</h3>
                <p style={{ color: C.textMuted, fontSize: '13px', margin: '6px 0 0', maxWidth: '640px' }}>
                    Compute → review → pin. The compute step proposes each unit&apos;s trailing-window numbers; you
                    commit the ones you trust. Baselines are pinned values the comparison view comes back to —
                    re-pinning a unit overwrites its previous pin.
                </p>
            </div>
            <button
                onClick={runCompute}
                disabled={computing}
                style={btn(computing ? C.border : C.primary, { cursor: computing ? 'not-allowed' : 'pointer' })}
            >
                {computing ? '⏳ Computing…' : `Compute proposed baselines (${weeks}w)`}
            </button>
        </div>
    )

    const roNote = readOnly && (
        <div
            style={{
                background: `${C.warning}18`,
                border: `1px solid ${C.warning}55`,
                borderRadius: '8px',
                color: C.warning,
                padding: '8px 12px',
                fontSize: '12px',
                marginBottom: '14px',
            }}
        >
            Pinning requires an admin/ops role — you can compute and review proposals, but not commit them.
        </div>
    )

    let proposalBody = null
    if (notConfigured) {
        proposalBody = (
            <div style={{ color: C.textMuted, fontSize: '13px', padding: '20px 0', textAlign: 'center' }}>
                Baselines need a database — none is configured for this environment.
            </div>
        )
    } else if (computeErr) {
        proposalBody = (
            <div
                style={{
                    background: `${C.danger}14`,
                    border: `1px solid ${C.danger}55`,
                    borderRadius: '8px',
                    color: C.danger,
                    padding: '8px 12px',
                    fontSize: '12px',
                    marginBottom: '14px',
                }}
            >
                {computeErr}
            </div>
        )
    } else if (proposal) {
        const props = proposal.proposals || []
        proposalBody =
            props.length === 0 ? (
                <div style={{ color: C.textMuted, fontSize: '13px', padding: '16px 0' }}>
                    No pinnable units in the last {proposal.weeks} weeks — no work items with a team/mini-pod and a
                    valid framework closed in the window.
                </div>
            ) : (
                <div>
                    <div style={{ color: C.textMuted, fontSize: '12px', marginBottom: '10px' }}>
                        Window: <strong style={{ color: C.text }}>{fmtDate(proposal.window_start)}</strong> →{' '}
                        <strong style={{ color: C.text }}>{fmtDate(proposal.window_end)}</strong> ({proposal.weeks}w) ·{' '}
                        {props.length} unit{props.length === 1 ? '' : 's'} proposed
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...th, width: '32px' }} />
                                    <th style={th}>Unit</th>
                                    <th style={th}>Framework</th>
                                    {METRIC_ORDER.map((m) => (
                                        <th key={m} style={{ ...th, textAlign: 'right' }}>
                                            {METRIC_LABEL[m]}
                                        </th>
                                    ))}
                                    <th style={{ ...th, textAlign: 'right' }}>n</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {props.map((p) => {
                                    const key = proposalKey(p)
                                    const byName = {}
                                    for (const m of p.metrics) byName[m.metric] = m.value
                                    const msg = rowMsg[key]
                                    const checked = !!selected[key]
                                    return (
                                        <tr key={key}>
                                            <td style={{ ...td, textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={readOnly || pinning}
                                                    onChange={() => toggle(key)}
                                                />
                                            </td>
                                            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                                <span style={{ color: C.text, fontWeight: 600 }}>{p.unit_key}</span>{' '}
                                                <span style={{ color: C.textSubtle, fontSize: '11px' }}>
                                                    {p.unit_type}
                                                </span>
                                            </td>
                                            <td style={{ ...td, color: C.textMuted, fontSize: '12px' }}>
                                                {p.framework}
                                            </td>
                                            {METRIC_ORDER.map((m) => (
                                                <td
                                                    key={m}
                                                    style={{
                                                        ...td,
                                                        textAlign: 'right',
                                                        fontVariantNumeric: 'tabular-nums',
                                                    }}
                                                >
                                                    {fmtMetric(m, byName[m])}
                                                </td>
                                            ))}
                                            <td style={{ ...td, textAlign: 'right', color: C.textMuted }}>
                                                {p.sample_n}
                                            </td>
                                            <td style={{ ...td, textAlign: 'right', fontSize: '11px' }}>
                                                {msg ? (
                                                    <span
                                                        style={{
                                                            color:
                                                                msg.type === 'ok'
                                                                    ? C.success
                                                                    : msg.type === 'err'
                                                                      ? C.danger
                                                                      : C.textMuted,
                                                        }}
                                                    >
                                                        {msg.text}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: C.textSubtle }}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                    {!readOnly && (
                        <div style={{ marginTop: '14px' }}>
                            <button
                                onClick={pinSelected}
                                disabled={pinning || selectedCount === 0}
                                style={btn(pinning || selectedCount === 0 ? C.border : C.success, {
                                    cursor: pinning || selectedCount === 0 ? 'not-allowed' : 'pointer',
                                })}
                            >
                                {pinning ? '⏳ Pinning…' : `Pin ${selectedCount} selected`}
                            </button>
                        </div>
                    )}
                </div>
            )
    }

    const pinsSection = (
        <div style={{ marginTop: proposal ? '24px' : '16px' }}>
            <div
                style={{
                    color: C.textMuted,
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    marginBottom: '8px',
                }}
            >
                Currently pinned
            </div>
            {pinsLoading ? (
                <div style={{ color: C.textMuted, fontSize: '13px', padding: '10px 0' }}>Loading pins…</div>
            ) : pins.length === 0 ? (
                <div style={{ color: C.textSubtle, fontSize: '13px', padding: '10px 0' }}>No baselines pinned yet.</div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                        <thead>
                            <tr>
                                <th style={th}>Unit</th>
                                <th style={th}>Framework</th>
                                <th style={th}>Metric</th>
                                <th style={{ ...th, textAlign: 'right' }}>Value</th>
                                <th style={{ ...th, textAlign: 'right' }}>n</th>
                                <th style={th}>Window</th>
                                <th style={th}>Pinned</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pins.map((b) => (
                                <tr key={b.id ?? `${b.unit_type}|${b.unit_key}|${b.metric}|${b.window_start}`}>
                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                        <span style={{ color: C.text }}>{b.unit_key}</span>{' '}
                                        <span style={{ color: C.textSubtle, fontSize: '11px' }}>{b.unit_type}</span>
                                    </td>
                                    <td style={{ ...td, color: C.textMuted, fontSize: '12px' }}>{b.framework}</td>
                                    <td style={{ ...td, color: C.textMuted, fontSize: '12px' }}>{b.metric}</td>
                                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                        {fmtMetric(b.metric, b.value)}
                                    </td>
                                    <td style={{ ...td, textAlign: 'right', color: C.textMuted }}>
                                        {b.sample_n ?? '—'}
                                    </td>
                                    <td style={{ ...td, color: C.textSubtle, fontSize: '11px', whiteSpace: 'nowrap' }}>
                                        {fmtDate(b.window_start)} → {fmtDate(b.window_end)}
                                    </td>
                                    <td style={{ ...td, color: C.textSubtle, fontSize: '11px', whiteSpace: 'nowrap' }}>
                                        {fmtDate(b.pinned_at)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )

    return (
        <div style={cardStyle}>
            {panelHeader}
            {roNote}
            {proposalBody}
            {pinsSection}
        </div>
    )
}
